import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const CHAT_SEND_ACTION_ID = 'chat.message.send'

type ChatSendArgs = { prompt: string }

export type ChatSendConfirmPayload = {
  kind: 'CHAT_SEND'
  channel: string | null
  message: string
}

const chatSendArgsSchema: JSONSchemaType<ChatSendArgs> = {
  type: 'object',
  properties: { prompt: { type: 'string' } },
  required: ['prompt'],
  additionalProperties: false,
}

const parseChannelAndMessage = (prompt: string): { channel: string | null; message: string } => {
  const channelMatch = prompt.match(/\bin\s+#?([\w-]+)\b/i)
  const toGroup = prompt.match(/\bto\s+(?:the\s+)?(team|group|channel|helper)\b/i)
  const channel = channelMatch?.[1] ?? toGroup?.[1] ?? null
  const messageMatch = prompt.match(/"([^"]+)"|'([^']+)'/)
  const bodyFromQuotes = messageMatch?.[1] ?? messageMatch?.[2] ?? null
  const bodyFallback = prompt
    .replace(/^\s*(post|send|dm|message|tell)\b/i, '')
    .replace(/\bin\s+#?[\w-]+\b/i, '')
    .replace(/\bto\s+(?:the\s+)?(team|group|channel|helper)\b/i, '')
    .trim()
  return { channel, message: bodyFromQuotes ?? bodyFallback }
}

const chatSendAction: ActionDefinition<ChatSendArgs> = {
  id: CHAT_SEND_ACTION_ID,
  label: 'Post to chat',
  description: 'Prepares a workspace chat message for explicit confirmation.',
  risk: 'LOW',
  domain: 'chat',
  surfaces: ['ai', 'catalog'],
  argsSchema: chatSendArgsSchema,
  permission: (context) => context.canPerform('chat.send'),
  run: async ({ prompt }, context): Promise<ActionOutcome> => {
    const { channel, message } = parseChannelAndMessage(prompt)
    if (!message.trim()) {
      return { kind: 'FAILED', message: 'I could not parse a message to send.', recoverable: true, correlationId: context.correlationId! }
    }
    const target = channel ? `#${channel}` : 'the default channel'
    const confirmPayload: ChatSendConfirmPayload = { kind: 'CHAT_SEND', channel, message }
    return {
      kind: 'NEEDS_CONFIRMATION',
      message: `Post to ${target}:\n"${message}"\n\nConfirm?`,
      confirmPayload,
      correlationId: context.correlationId!,
    }
  },
}

export const CHAT_MESSAGE_COPY_ACTION_ID = 'chat.message.copy'
export const CHAT_MESSAGE_DELETE_ACTION_ID = 'chat.message.delete'
export const CHAT_MESSAGE_REPLY_ACTION_ID = 'chat.message.reply'
export const CHAT_MESSAGE_CONVERT_TASK_ACTION_ID = 'chat.message.convert-to-task'
export const CHAT_CHANNEL_RENAME_ACTION_ID = 'chat.channel.rename'
export const CHAT_CHANNEL_NOTIFICATIONS_ACTION_ID = 'chat.channel.notifications'
export const CHAT_CHANNEL_LEAVE_ACTION_ID = 'chat.channel.leave'
export const CHAT_MENTION_PROFILE_ACTION_ID = 'chat.mention.open-profile'
export const CHAT_MENTION_DM_ACTION_ID = 'chat.mention.dm'

export type ChatOrbitTargetArgs = {
  id?: string
  title?: string
}

const chatOrbitTargetArgsSchema: JSONSchemaType<ChatOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const chatMessageCopyAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MESSAGE_COPY_ACTION_ID,
  label: 'Copy message',
  description: 'Copies chat message text to the clipboard.',
  risk: 'READ_ONLY',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  run: async ({ title }, context): Promise<ActionOutcome> => {
    if (!title) {
      context.showToast?.('No message text to copy', 'warning')
      return {
        kind: 'FAILED',
        message: 'No message text to copy',
        recoverable: true,
        correlationId: context.correlationId!,
      }
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(title)
        context.showToast?.('Copied', 'success')
        return {
          kind: 'COMPLETED',
          result: { copied: true, text: title },
          message: 'Copied',
          correlationId: context.correlationId!,
        }
      } catch {
        context.showToast?.('Copy failed', 'error')
        return {
          kind: 'FAILED',
          message: 'Copy failed',
          recoverable: true,
          correlationId: context.correlationId!,
        }
      }
    }
    context.showToast?.('Clipboard not available', 'warning')
    return {
      kind: 'FAILED',
      message: 'Clipboard not available',
      recoverable: false,
      correlationId: context.correlationId!,
    }
  },
}

const chatMessageDeleteAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MESSAGE_DELETE_ACTION_ID,
  label: 'Delete message',
  description: 'Deletes a chat message from the active channel.',
  risk: 'MEDIUM',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.send'),
  run: async ({ id }, context): Promise<ActionOutcome> => {
    if (!id) {
      return {
        kind: 'FAILED',
        message: 'No message ID provided',
        recoverable: false,
        correlationId: context.correlationId!,
      }
    }
    if (context.chatOps?.deleteMessage) {
      try {
        await context.chatOps.deleteMessage(id)
        context.showToast?.('Message deleted', 'success')
        return {
          kind: 'COMPLETED',
          result: { deleted: true, messageId: id },
          message: 'Message deleted',
          correlationId: context.correlationId!,
        }
      } catch (err) {
        context.showToast?.('Delete failed', 'error')
        return {
          kind: 'FAILED',
          message: err instanceof Error ? err.message : 'Delete failed',
          recoverable: true,
          correlationId: context.correlationId!,
        }
      }
    }
    return {
      kind: 'FAILED',
      message: 'Chat service not available in this context',
      recoverable: false,
      correlationId: context.correlationId!,
    }
  },
}

const chatMessageReplyAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MESSAGE_REPLY_ACTION_ID,
  label: 'Reply',
  description: 'Prepares a reply to the selected chat message.',
  risk: 'READ_ONLY',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.send'),
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Reply'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.replyToMessage) {
      const message = `Operation for Reply (${CHAT_MESSAGE_REPLY_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.replyToMessage(id, title))
      context.showToast?.(`Reply${title ? ` · ${title}` : ''}`, 'info')
      return {
        kind: 'COMPLETED',
        result: { replyToId: id },
        message: 'Reply',
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Reply failed'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
  },
}

const chatMessageConvertTaskAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MESSAGE_CONVERT_TASK_ACTION_ID,
  label: 'Convert to task',
  description: 'Converts a chat message into a workspace task.',
  risk: 'LOW',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Convert to task'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.convertToTask) {
      const message = `Operation for Convert to task (${CHAT_MESSAGE_CONVERT_TASK_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.convertToTask(id, title))
      context.showToast?.(`Convert to task${title ? ` · ${title}` : ''}`, 'info')
      return {
        kind: 'COMPLETED',
        result: { messageId: id },
        message: 'Converted to task',
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Convert to task failed'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
  },
}

const chatChannelRenameAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_CHANNEL_RENAME_ACTION_ID,
  label: 'Rename channel',
  description: 'Renames the chat channel.',
  risk: 'LOW',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.create_channel'),
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Rename channel'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.renameChannel) {
      const message = `Operation for Rename channel (${CHAT_CHANNEL_RENAME_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.renameChannel(id, title ?? ''))
      context.showToast?.('Channel renamed', 'success')
      return {
        kind: 'COMPLETED',
        result: { channelId: id, name: title },
        message: 'Channel renamed',
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Rename failed'
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const chatChannelNotificationsAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_CHANNEL_NOTIFICATIONS_ACTION_ID,
  label: 'Channel notifications',
  description: 'Configures notification settings for this channel.',
  risk: 'READ_ONLY',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.view'),
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Channel notifications'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.configureNotifications) {
      const message = `Operation for Channel notifications (${CHAT_CHANNEL_NOTIFICATIONS_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.configureNotifications(id))
      context.showToast?.(`Notifications${title ? ` · ${title}` : ''}`, 'info')
      return {
        kind: 'COMPLETED',
        result: { channelId: id },
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Notifications config failed'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
  },
}

const chatChannelLeaveAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_CHANNEL_LEAVE_ACTION_ID,
  label: 'Leave channel',
  description: 'Leaves the selected chat channel.',
  risk: 'MEDIUM',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.view'),
  run: async ({ id }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Leave channel'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.leaveChannel) {
      const message = `Operation for Leave channel (${CHAT_CHANNEL_LEAVE_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.leaveChannel(id))
      context.showToast?.('Left channel', 'success')
      return {
        kind: 'COMPLETED',
        result: { left: true, channelId: id },
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Leave failed'
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: true,
        correlationId,
      }
    }
  },
}

const chatMentionOpenProfileAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MENTION_PROFILE_ACTION_ID,
  label: 'Open profile',
  description: 'Opens the member profile from a mention.',
  risk: 'READ_ONLY',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.view'),
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Open profile'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.navigate) {
      const message = `Operation for Open profile (${CHAT_MENTION_PROFILE_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    const path = `/console/team?member=${encodeURIComponent(id)}`
    context.navigate(path)
    context.showToast?.(`Open profile${title ? ` · ${title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { memberId: id },
      navigateTo: path,
      correlationId,
    }
  },
}

const chatMentionDmAction: ActionDefinition<ChatOrbitTargetArgs> = {
  id: CHAT_MENTION_DM_ACTION_ID,
  label: 'Direct message',
  description: 'Opens or starts a direct message with a mentioned member.',
  risk: 'LOW',
  domain: 'chat',
  surfaces: ['orbit', 'catalog'],
  argsSchema: chatOrbitTargetArgsSchema,
  permission: (context) => context.canPerform('chat.send'),
  run: async ({ id, title }, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'
    if (!id) {
      const message = 'Target ID is required for Direct message'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    if (!context.chatOps?.openDm) {
      const message = `Operation for Direct message (${CHAT_MENTION_DM_ACTION_ID}) is not available`
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
    try {
      await Promise.resolve(context.chatOps.openDm(id))
      context.showToast?.(`DM${title ? ` · ${title}` : ''}`, 'info')
      return {
        kind: 'COMPLETED',
        result: { dmUserId: id },
        correlationId,
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Direct message failed'
      context.showToast?.(message, 'error')
      return { kind: 'FAILED', message, recoverable: false, correlationId }
    }
  },
}

export const registerChatActions = (): void => {
  if (!getAction(CHAT_SEND_ACTION_ID)) registerAction(chatSendAction)
  if (!getAction(CHAT_MESSAGE_COPY_ACTION_ID)) registerAction(chatMessageCopyAction)
  if (!getAction(CHAT_MESSAGE_DELETE_ACTION_ID)) registerAction(chatMessageDeleteAction)
  if (!getAction(CHAT_MESSAGE_REPLY_ACTION_ID)) registerAction(chatMessageReplyAction)
  if (!getAction(CHAT_MESSAGE_CONVERT_TASK_ACTION_ID)) registerAction(chatMessageConvertTaskAction)
  if (!getAction(CHAT_CHANNEL_RENAME_ACTION_ID)) registerAction(chatChannelRenameAction)
  if (!getAction(CHAT_CHANNEL_NOTIFICATIONS_ACTION_ID)) registerAction(chatChannelNotificationsAction)
  if (!getAction(CHAT_CHANNEL_LEAVE_ACTION_ID)) registerAction(chatChannelLeaveAction)
  if (!getAction(CHAT_MENTION_PROFILE_ACTION_ID)) registerAction(chatMentionOpenProfileAction)
  if (!getAction(CHAT_MENTION_DM_ACTION_ID)) registerAction(chatMentionDmAction)
}

export const registerChatSendAction = (): void => {
  registerChatActions()
}

registerChatActions()
